/**
 * Pure helpers for the chart's "jump to an edge of the loaded data" controls.
 *
 * Lightweight Charts logical indices are relative to the loaded series: index 0
 * is the oldest loaded bar, so the left edge of loaded history is always at
 * logical 0 no matter how many bars have been lazy-loaded in behind it. That
 * makes the oldest-bar maths independent of the candle count, which is why it
 * lives here as plain functions instead of inside the chart component.
 */

/** Bars of slack before we stop calling the view "at the oldest loaded bar". */
export const OLDEST_EDGE_TOLERANCE_BARS = 3;

/** Bars to show when we have no current range to copy the zoom level from. */
export const DEFAULT_VISIBLE_SPAN_BARS = 120;

/**
 * One bar of gutter to the left of the oldest candle, so landing there reads as
 * "this is the start of the data" rather than "the chart is clipped".
 */
const OLDEST_LEFT_GUTTER_BARS = 1;

export interface LogicalRange {
  from: number;
  to: number;
}

/**
 * True when the oldest loaded bar is already on screen (within tolerance), i.e.
 * the "go to oldest" button would be a no-op and should not be rendered.
 *
 * An unknown range (chart not ready, no data) counts as "already there" so the
 * button stays hidden rather than flashing up with nothing to scroll to.
 */
export function isAtOldestLoadedBar(
  visibleFrom: number | null | undefined,
  toleranceBars: number = OLDEST_EDGE_TOLERANCE_BARS
): boolean {
  if (visibleFrom == null || !Number.isFinite(visibleFrom)) return true;
  return visibleFrom <= toleranceBars;
}

/**
 * The logical range that puts the oldest loaded bar at the left edge while
 * keeping the user's current zoom (bar span), so the jump changes position
 * only — never how zoomed in they were.
 */
export function oldestLogicalRange(
  current: LogicalRange | null | undefined,
  fallbackSpanBars: number = DEFAULT_VISIBLE_SPAN_BARS
): LogicalRange {
  const span = spanOf(current, fallbackSpanBars);
  const from = -OLDEST_LEFT_GUTTER_BARS;
  return { from, to: from + span };
}

/**
 * How far into the view the go-to-date jump places its target bar.
 *
 * Not the left edge: landing a target hard against the edge gives no sense of
 * what led into it, and any small imprecision in which bar was picked pushes it
 * off screen entirely. A quarter in keeps the picked moment obviously visible
 * with context on both sides.
 */
export const TARGET_LEAD_FRACTION = 0.25;

/**
 * The logical range that brings bar `index` into view at
 * {@link TARGET_LEAD_FRACTION} across, preserving the current zoom.
 *
 * Same contract as {@link oldestLogicalRange} — position changes, zoom does
 * not — and the range is deliberately NOT clamped to the loaded series: the
 * chart tolerates negative logical indices (that is how the oldest-bar jump
 * works), so a target near either end still lands correctly with empty gutter
 * beside it instead of being shoved back inside.
 */
export function targetLogicalRange(
  index: number,
  current: LogicalRange | null | undefined,
  fallbackSpanBars: number = DEFAULT_VISIBLE_SPAN_BARS,
  leadFraction: number = TARGET_LEAD_FRACTION
): LogicalRange {
  const span = spanOf(current, fallbackSpanBars);
  const safeIndex = Number.isFinite(index) ? index : 0;
  const lead = Number.isFinite(leadFraction) ? Math.min(Math.max(leadFraction, 0), 1) : 0;
  const from = safeIndex - span * lead;
  return { from, to: from + span };
}

/** Current bar span, falling back when the range is absent or degenerate. */
function spanOf(current: LogicalRange | null | undefined, fallbackSpanBars: number): number {
  const currentSpan =
    current && Number.isFinite(current.from) && Number.isFinite(current.to)
      ? current.to - current.from
      : NaN;
  return Number.isFinite(currentSpan) && currentSpan > 0 ? currentSpan : fallbackSpanBars;
}
